import { storeToRefs } from 'pinia';
import { readonly } from 'vue';

import { useDyChooseResults } from '@/composables/dynamic-yield/experiences';
import { FeatureFlagsChoice } from '@/lib/personalization/dynamicYield';
import {
  FeatureFlags,
  FlagsByLayer,
  internalExperiments,
  Layer,
  layers,
  useFeatureFlags as useFeatureFlagsStore,
} from '@/stores/featureFlags';
import { dyEvent, gaEvent } from '@/utils/analytics';
import { isDefined } from '@/utils/isDefined';
import { getCookie, setCookie } from '@/utils/isomorphic/cookie';

export function fromFlagSpec(flagSpec: string): FeatureFlags {
  const entries = flagSpec.split(',').map((spec) => {
    const [_, setting, name] = /^(-?)(.+)$/.exec(spec)!;
    return [name, setting !== '-'];
  });
  return Object.fromEntries(entries);
}

export function toFlagSpec(flags: FeatureFlags, flagNames?: (keyof FeatureFlags)[]): string {
  return (flagNames ?? Object.keys(flags).sort())
    .map((name) => `${flags[name] ? '' : '-'}${name}`)
    .join();
}

export function useFeatureFlags() {
  const { choicesByName, loadExperiences } = useDyChooseResults<FeatureFlagsChoice>([]);

  const { flags, flagsByLayer, flagsMetadata } = storeToRefs(useFeatureFlagsStore());

  /**
   * Update the manually-maintained reactive `flags` object to match the current
   * state of the `flagsByLayer` object. Called by replaceFlags() and setFlags().
   */
  function updateReactiveFlags(updatedKeys: string[]) {
    updatedKeys.forEach((key) => {
      const newValue = layers.flatMap((l) => flagsByLayer.value[l][key] ?? [])[0];
      if (newValue === undefined) {
        delete flags.value[key];
      } else if (flags.value[key] !== newValue) {
        flags.value[key] = newValue;
      }
    });
  }

  /**
   * Replace all flags in all layers with the given flags. Meant for use client
   * side when a whole new set is received from the server, such as after
   * sign-in or other identification change.
   */
  function replaceFlagsByLayer(newFlagsByLayer: FlagsByLayer) {
    // update the canonical layered flag sets
    layers.forEach((layer) => {
      flagsByLayer.value[layer] = newFlagsByLayer[layer] ?? {};
    });

    // update the reactive object to match
    // (examine all flag keys, old and new, to make sure none are accidentally left around)
    const allKeys = [flags.value, ...Object.values(flagsByLayer.value)].flatMap(Object.keys);
    const uniqueKeys = [...new Set(allKeys)];
    updateReactiveFlags(uniqueKeys);
  }

  /**
   * Set one or more specific flags in the given layer (merging with existing
   * flags) and propagate changes to the server be persisted. Used by
   * loadDyFlags().
   */
  function setFlags(updates: FeatureFlags, layer: Layer = 'local') {
    // update the canonical layered flag sets
    const updatedLayer = { ...flagsByLayer.value[layer], ...updates };
    Object.keys(updates).forEach((key) => updates[key] === undefined && delete updatedLayer[key]);
    flagsByLayer.value[layer] = updatedLayer;

    // update the reactive object to match
    updateReactiveFlags(Object.keys(updates));
  }

  /**
   * Return a getter function for the value of a flag from the reactive `flags` object.
   * If the flag is defined as an internal experiment and not already set, it will
   * be randomly assigned a value once. The decision metadata is stored in `flagsMetadata`.
   *
   * Call sites that need reactivity must combine Vue's `computed()` with `toValue(); _e.g._
   *
   * ```ts
   * const flag = computed(() => toValue(getFlag('someFlag')));
   * ```
   */
  function getFlag(flagName: keyof FeatureFlags): () => FeatureFlags[keyof FeatureFlags] {
    return () => {
      let value = flags.value[flagName];

      if (flagName in internalExperiments) {
        if (!isDefined(value)) {
          value = Math.random() > 0.5;
          setFlags({ [flagName]: value });
        }

        rememberInternalFlag(flagName, value);

        flagsMetadata.value[flagName] = {
          ...internalExperiments[flagName],
          variationId: value,
          variationName: value ? 'experiment' : 'control',
        };
      }

      return value;
    };
  }

  /**
   * Load visitor-level flags from a DY campaign
   *
   * DY will be queried if and only if we don't already have a value for at
   * least one of the listed flags. Flags are expected to be in a `flags` field
   * of the DY API campaign variation. After awaiting this promise, look at the
   * `flags` reactive object as usual for the latest flags.
   *
   * @param selector  API Selector of the DY campaign that will (hopefully)
   * provide at least one of the flags in question
   * @param flagNames One or more flags that we hope DY can tell us about
   * @param options Options to alter default behavior
   * @param options.skipExperimentEvent Whether to skip sending an experiment viewed event (default behavior)
   */
  async function loadDyFlags(
    selector: string,
    flagNames: (keyof FeatureFlags)[],
    options?: {
      skipExperimentEvent?: boolean;
    },
  ) {
    if (flagNames.some((name) => name in flags.value)) {
      return;
    }

    let variation = choicesByName[selector]?.variations[0];
    if (!variation) {
      await loadExperiences({
        newExperiences: [selector],
        skipExperimentEvent: options?.skipExperimentEvent,
        skipLoadedExperiences: true,
      });
      variation = choicesByName[selector]?.variations[0];
    }

    const data = variation?.payload.data;
    if (data?.flags && typeof data.flags === 'string') {
      if (variation?.analyticsMetadata) {
        flagNames.forEach((flagName) => {
          flagsMetadata.value[flagName] = {
            ...variation.analyticsMetadata,
            source: 'Dynamic Yield',
            type: 'external',
          };
        });
      }

      setFlags(fromFlagSpec(data.flags), 'visitor');
    }

    if (data?.dyEvent && typeof data.dyEvent === 'object') {
      dyEvent(data.dyEvent);
    }

    if (data?.gaEvent && typeof data.gaEvent === 'object') {
      gaEvent(data.gaEvent);
    }
  }

  /**
   * Narrowly scoped to internal experiments, including multiple, via the flag spec. Cookie key
   * modeled after the Builder.io pattern of `builder.tests.*`, though they create a unique cookie
   * for each content entry; we can easily juggle multiple flags at a time.
   *
   * Expect webstore to clear this cookie upon subsequent requests.
   */
  function rememberInternalFlag(flagName: keyof FeatureFlags, value: boolean) {
    if (import.meta.env.SSR || !(flagName in flagsByLayer.value.local)) return;

    const cookieKey = 'webstore.tests.fromWebfront';
    const currentValue = getCookie(cookieKey) ?? '';
    const rememberedFlags = currentValue ? fromFlagSpec(currentValue) : {};
    if (!(flagName in rememberedFlags)) {
      const flagSpecs = toFlagSpec({ ...rememberedFlags, [flagName]: value });
      setCookie(cookieKey, flagSpecs, { expiresInMonths: 1 });
    }
  }

  return {
    flags: readonly(flags.value),
    flagsMetadata: readonly(flagsMetadata.value),
    getFlag,
    loadDyFlags,
    rememberInternalFlag,
    replaceFlagsByLayer,
    setFlags,
  };
}
